import {fromFileReference} from './fileReference';

type TaskComments = {edges?: ({node?: {comment?: string | null}} | null)[]} | null;
type TaskLike = {description?: string | null; comments?: TaskComments};

export type ReferenceSites = {description: Set<string>; comments: Set<string>};
export type SurfaceFlags = {description: boolean; comments: boolean};

/** Extracts the file ids of every inline image reference in one stored rich-text value. */
export const collectReferencedFileIds = (content: string | null | undefined): Set<string> => {
	const ids = new Set<string>();
	if (!content) return ids;

	let parsed: {entityMap?: Record<string, {type?: string; data?: {src?: string}} | null>};
	try {
		parsed = JSON.parse(content);
	} catch {
		// Legacy or partially-migrated content may not be valid JSON. It cannot
		// contain one of our references if it cannot be parsed, so report none
		// rather than letting a parse error escape into a render path.
		return ids;
	}

	Object.values(parsed?.entityMap ?? {}).forEach(entity => {
		if (entity?.type?.toUpperCase() !== 'IMAGE') return;
		const fileId = fromFileReference(entity?.data?.src ?? '');
		if (fileId) ids.add(fileId);
	});

	return ids;
};

/**
 * Where each file is referenced across a task.
 *
 * Description and comments are kept apart because they are gated by different
 * feature flags, and the visibility rule depends on which surfaces can actually
 * render the image.
 */
export const referenceSitesForTask = (task: TaskLike): ReferenceSites => ({
	description: collectReferencedFileIds(task?.description),
	comments: new Set((task?.comments?.edges ?? []).flatMap(edge => [...collectReferencedFileIds(edge?.node?.comment)])),
});

/** File ids present in the old content and absent from the new one. */
export const removedReferences = (before: string | null | undefined, after: string | null | undefined): string[] => {
	const stillThere = collectReferencedFileIds(after);
	return [...collectReferencedFileIds(before)].filter(id => !stillThere.has(id));
};

/**
 * Whether a file appears in the task's Files list, and whether it may be deleted there.
 *
 * A description image is listed like any attachment but cannot be deleted from
 * the list — the text owns it, so removing it from the text is the only way to
 * remove it, which is what makes broken references impossible. A comment image
 * is not listed at all.
 *
 * Both rules invert as soon as ANY surface referencing the file cannot render
 * it, because a legacy editor can neither show nor remove an image and the
 * Files list is then the only way to reach the file. Evaluating every site
 * before deciding — rather than returning on the first match — is what stops a
 * file referenced in both places under mixed flags becoming invisible in the
 * list and unremovable from the text at the same time.
 */
export const fileVisibility = (
	fileId: string,
	sites: ReferenceSites,
	flags: SurfaceFlags
): {visible: boolean; canDelete: boolean} => {
	const referencingSites = [
		{referenced: sites.description.has(fileId), rendered: flags.description, listed: true},
		{referenced: sites.comments.has(fileId), rendered: flags.comments, listed: false},
	].filter(site => site.referenced);

	// Not referenced anywhere: an ordinary attachment, untouched.
	if (referencingSites.length === 0) return {visible: true, canDelete: true};

	// Referenced somewhere that cannot render it: the file would otherwise be
	// unreachable, so hand it back as a normal attachment.
	if (referencingSites.some(site => !site.rendered)) return {visible: true, canDelete: true};

	// Every referencing surface renders it, so the text owns it. It is listed only
	// if the description is one of those surfaces.
	return {visible: referencingSites.every(site => site.listed), canDelete: false};
};
